// Rollout Readout: React UI on the HeroResultLayout template. The maths lives in
// ./model.js. The headline is the change in the rate, named for what the data can
// support: a change after the launch, or an effect when the split was random.

import { useMemo, useState } from "react";
import { HeroResultLayout, HeroSection } from "@/components/templates";
import {
  BarFooter,
  BriefLines,
  Callout,
  ExampleDataPicker,
  Explainer,
  Field,
  FieldGrid,
  MetricCard,
  MetricGrid,
  PillToggle,
  ResultCard,
  SectionHead,
  UrlStateNotices,
  useUrlRestore,
  useUrlSync,
} from "@/components/layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { headingFor, ledeOf } from "@/lib/pageHeadings";
import { analyseRollout, pct, signedPct, summaryLines } from "./model.js";
import { EXAMPLES, exampleById } from "./examples.js";
import { rolloutShare } from "./share.js";

const HEADING = headingFor("tool/rollout-readout");

/* eslint-disable @typescript-eslint/no-explicit-any */
type Any = any;

interface Group {
  events: number;
  exposure: number;
}
interface State {
  mode: string;
  history: string;
  afterPeriods: number;
  eventLabel: string;
  exposureLabel: string;
  base: Group;
  test: Group;
  driftPct: number;
  higherIsBetter: boolean;
  targetPct: number;
}

const INITIAL = EXAMPLES[0].state as State;
const toNumber = (text: string) => (text.trim() === "" ? NaN : Number(text));
const show = (x: number) => (Number.isFinite(x) ? String(x) : "");

export function App() {
  const shared = useUrlRestore<State>(rolloutShare, (s) => setState(s));
  const [state, setState] = useState<State>(shared.initial ?? INITIAL);
  useUrlSync(rolloutShare, () => state, [state], shared);
  const set = (patch: Partial<State>) => setState((s) => ({ ...s, ...patch }));
  const r: Any = useMemo(() => analyseRollout(state), [state]);
  const ok = !r.issues.length;
  const randomized = state.mode === "randomized";
  const label = randomized ? "Effect of the feature" : "Change after the launch";

  const measured = r.driftFrom === "measured" || (r.history?.periods?.length ?? 0) >= 2;
  const groupFields = (key: "base" | "test", name: string) =>
    key === "base" && measured ? (
      <Field label={`${name}: total of the pasted periods`}>
        <p className="m-0 py-2 font-mono text-sm">
          {r.base.events} {state.eventLabel} out of {r.base.exposure} {state.exposureLabel}
        </p>
      </Field>
    ) : (
    <>
      <Field label={`${name}: ${state.eventLabel}`}>
        <Input type="number" min={0} step={1} aria-label={`${name} events`} value={show(state[key].events)} onChange={(e) => set({ [key]: { ...state[key], events: toNumber(e.target.value) } } as Partial<State>)} />
      </Field>
      <Field label={`${name}: ${state.exposureLabel}`}>
        <Input type="number" min={0} step="any" aria-label={`${name} exposure`} value={show(state[key].exposure)} onChange={(e) => set({ [key]: { ...state[key], exposure: toNumber(e.target.value) } } as Partial<State>)} />
      </Field>
    </>
  );

  return (
    <HeroResultLayout
      label="Unit 42 / Rollout readout"
      tags={<span className="hidden md:inline">Local · nothing leaves the page</span>}
      eyebrow="Launch impact without an A/B test"
      title={HEADING.h1}
      lede={ledeOf(HEADING)}
      result={
        <ResultCard
          label={label}
          value={ok ? signedPct(r.median) : "—"}
          caption={ok ? `90% range ${signedPct(r.low)} to ${signedPct(r.high)} in ${state.eventLabel} per ${state.exposureLabel}` : "Fix the counts below"}
          fraction={ok ? r.chanceBetter : 0}
        />
      }
      sticky={ok ? { label, value: signedPct(r.median), detail: `${signedPct(r.low)} to ${signedPct(r.high)}` } : undefined}
      footer={
        <BarFooter>
          <span>{randomized ? "Randomized split · Gamma–Poisson" : "Before/after · Gamma–Poisson · drift allowance"}</span>
          <span>{ok ? `${pct(r.chanceBetter)} chance it moved the right way` : "Counts incomplete"}</span>
        </BarFooter>
      }
    >
      <HeroSection>
        <Explainer id="rollout" title="What this answers" summary="Whether a launch moved a number" defaultOpen>
          <p>
            Without an A/B platform, most teams compare a rate <strong>before and after</strong> a launch. That shows
            a change after the launch — but the season, a campaign or another release changed things too. This page
            keeps that honest with a drift allowance that more data never shrinks.
          </p>
          <p>
            If you roll out to <strong>accounts picked at random</strong> — for example by a hash of their id — the
            two groups differ only by the feature, and the page reports an effect with no drift term.
          </p>
        </Explainer>
        <h2 className="sr-only">Examples</h2>
        <UrlStateNotices state={shared} />
        <ExampleDataPicker
          examples={EXAMPLES.map(({ id, name, description }) => ({ id, name, description }))}
          onLoad={(id) => {
            const ex = exampleById(id);
            if (ex) setState(ex.state as State);
          }}
          deepLink={!shared.initial}
        />
        <p className="mb-0 mt-3 text-xs text-muted-foreground">Examples use synthetic counts.</p>
      </HeroSection>

      <HeroSection>
        <SectionHead step={1} label="Comparison" title="How was the feature rolled out?" />
        <PillToggle
          aria-label="How was the feature rolled out"
          value={state.mode}
          onChange={(mode) => set({ mode })}
          options={[
            { value: "before-after", label: "Before and after" },
            { value: "randomized", label: "Accounts picked at random" },
          ]}
        />
        <FieldGrid className="mt-5">
          <Field label="What is counted" hint="e.g. activations">
            <Input aria-label="What is counted" maxLength={60} value={state.eventLabel} onChange={(e) => set({ eventLabel: e.target.value })} />
          </Field>
          <Field label="Out of" hint="e.g. signups, account-weeks">
            <Input aria-label="What the count is out of" maxLength={60} value={state.exposureLabel} onChange={(e) => set({ exposureLabel: e.target.value })} />
          </Field>
          {groupFields("base", r.names[0])}
          {groupFields("test", r.names[1])}
        </FieldGrid>
        {!randomized && (
          <div className="mt-5">
            <Field label="Before the launch, period by period" hint="One line per week (or day): events/exposure, e.g. 102/410">
              <Textarea
                aria-label="Pre-launch periods"
                className="min-h-24 font-mono text-sm"
                placeholder={"102/410\n95/398\n110/421"}
                value={state.history}
                onChange={(e) => set({ history: e.target.value })}
                spellCheck={false}
              />
            </Field>
            <p className="mb-0 mt-2 text-sm text-muted-foreground">
              A rate moves from week to week even when nothing launches. These periods measure how much, so the page
              can tell a launch from an ordinary week. Use periods of the same length as the after window&rsquo;s.
            </p>
            {measured && (
              <Field label="The after window covers" hint="Periods of the same length" className="mt-3 max-w-xs">
                <Input
                  type="number"
                  min={1}
                  max={520}
                  step={1}
                  aria-label="Periods in the after window"
                  value={String(state.afterPeriods)}
                  onChange={(e) => set({ afterPeriods: Math.min(520, Math.max(1, Math.round(Number(e.target.value) || 1))) })}
                />
              </Field>
            )}
            {r.history?.excluded?.length > 0 && (
              <Callout kind="warn" className="mt-3">
                <strong>Not counted:</strong>{" "}
                {r.history.excluded.map((x: Any) => `"${x.line}" (${x.reason})`).join("; ")}.
              </Callout>
            )}
          </div>
        )}
        <FieldGrid className="mt-5">
          <Field label="More is better">
            <PillToggle
              aria-label="Which direction is better"
              value={state.higherIsBetter ? "up" : "down"}
              onChange={(v) => set({ higherIsBetter: v === "up" })}
              options={[
                { value: "up", label: "More is better" },
                { value: "down", label: "Fewer is better" },
              ]}
            />
          </Field>
          <Field label="Smallest change that matters" hint="Percent">
            <Input type="number" min={0} step="any" aria-label="Smallest change that matters" value={String(state.targetPct)} onChange={(e) => set({ targetPct: Math.max(0, Number(e.target.value) || 0) })} />
          </Field>
          {!randomized && (
            <Field
              label={measured ? "Extra allowance for one-off changes, ±" : "Everything else could move it by ±"}
              hint={measured ? "Percent; campaigns, price changes the history cannot show" : "Percent, 90% range"}
            >
              <Input type="number" min={0} max={200} step="any" aria-label="Drift allowance" value={String(state.driftPct)} onChange={(e) => set({ driftPct: Math.min(200, Math.max(0, Number(e.target.value) || 0)) })} />
            </Field>
          )}
        </FieldGrid>
        {!ok && (
          <Callout kind="error" className="mt-4">
            {r.issues.join(" ")}
          </Callout>
        )}
      </HeroSection>

      {ok && (
        <HeroSection>
          <SectionHead step={2} label="Reading" title={randomized ? "What the feature did" : "What changed after the launch"} />
          {!randomized && (
            <Callout kind="warn" className="mb-4">
              <strong>A change after the launch is not an effect of the launch.</strong>{" "}
              {r.driftFrom === "measured"
                ? `Before the launch the rate swung about ±${Math.round(r.swingPct)}% from period to period. The range includes that swing${state.driftPct > 0 ? ` and your extra ±${state.driftPct}%` : ""}: ${pct(r.driftShare)} of its width.`
                : `No pre-launch periods are given, so the range rests on your judged ±${state.driftPct}% for everything else, not on measured variation. Paste weekly counts from before the launch to measure it.`}
            </Callout>
          )}
          <MetricGrid className="lg:grid-cols-3">
            <MetricCard accent eyebrow={label} value={signedPct(r.median)} data-value={r.median}>
              90% range {signedPct(r.low)} to {signedPct(r.high)}.
            </MetricCard>
            <MetricCard eyebrow="Moved the right way" value={pct(r.chanceBetter)}>
              Chance the rate went {state.higherIsBetter ? "up" : "down"} at all.
            </MetricCard>
            <MetricCard eyebrow={`By more than ${state.targetPct}%`} value={pct(r.chanceMatters)}>
              Harm beyond {state.targetPct}%: {pct(r.chanceHarm)}.
            </MetricCard>
          </MetricGrid>
          <Callout kind="note" className="mt-6">
            <strong>Counts alone</strong> give {signedPct(r.dataOnly.low)} to {signedPct(r.dataOnly.high)}. Rates:{" "}
            {r.names[0].toLowerCase()} {r.rates.base.toFixed(4)}, {r.names[1].toLowerCase()} {r.rates.test.toFixed(4)} {state.eventLabel} per {state.exposureLabel}.
            Jeffreys Gamma posteriors on each rate.
          </Callout>
          <BriefLines className="mt-6" lines={summaryLines(r, state)} />
        </HeroSection>
      )}
    </HeroResultLayout>
  );
}
